import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import {
  ChevronDown, ChevronRight, Download, FileText, Trophy, Clock,
  Target, Users, CheckCircle, XCircle, Minus, Loader2
} from 'lucide-react';

const SUBJECT_COLORS = {
  Mathematics: 'bg-blue-100 text-blue-700',
  Physics: 'bg-green-100 text-green-700',
  Chemistry: 'bg-amber-100 text-amber-700',
};

export default function AdminResultsPage() {
  const [tests, setTests] = useState([]);
  const [selectedTest, setSelectedTest] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({});
  const [page, setPage] = useState(1);
  const [expandedStudent, setExpandedStudent] = useState(null);
  const [expandedQuestions, setExpandedQuestions] = useState(null);
  const [questionsLoading, setQuestionsLoading] = useState(false);

  useEffect(() => {
    fetchTests();
  }, []);

  useEffect(() => {
    if (selectedTest) fetchResults();
  }, [selectedTest, page]);

  const fetchTests = async () => {
    try {
      const { data } = await api.get('/results/admin/tests');
      setTests(data.data);
    } catch {
      toast.error('Failed to load tests');
    }
  };

  const fetchResults = async () => {
    setLoading(true);
    setExpandedStudent(null);
    setExpandedQuestions(null);
    try {
      const { data } = await api.get(`/results/admin/${selectedTest}?page=${page}&limit=50`);
      setResults(data.data);
      setPagination(data.pagination);
    } catch {
      toast.error('Failed to load results');
    } finally {
      setLoading(false);
    }
  };

  const fetchStudentQuestions = async (studentId) => {
    if (expandedStudent === studentId) {
      setExpandedStudent(null);
      setExpandedQuestions(null);
      return;
    }
    setQuestionsLoading(true);
    setExpandedStudent(studentId);
    setExpandedQuestions(null);
    try {
      const { data } = await api.get(`/results/admin/${selectedTest}/${studentId}`);
      setExpandedQuestions(data.data);
    } catch {
      toast.error('Failed to load response sheet');
      setExpandedStudent(null);
    } finally {
      setQuestionsLoading(false);
    }
  };

  const downloadCSV = async (studentId, hallTicket) => {
    try {
      const res = await api.get(`/results/admin/${selectedTest}/${studentId}/csv`, {
        responseType: 'blob'
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ResponseSheet_${hallTicket}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch {
      toast.error('Failed to download CSV');
    }
  };

  const selectedTestObj = tests.find(t => t._id === selectedTest);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Student Response Sheets</h1>
        <p className="text-gray-500 mt-1">View and download individual student response sheets</p>
      </div>

      {/* Test selector */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">Select Test</label>
        <div className="relative">
          <select
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black appearance-none bg-white pr-10"
            value={selectedTest || ''}
            onChange={e => { setSelectedTest(e.target.value || null); setPage(1); }}
          >
            <option value="">-- Choose a test --</option>
            {tests.map(t => (
              <option key={t._id} value={t._id}>
                {t.title} ({t.submissions} submissions)
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={16} />
        </div>
      </div>

      {/* Results table */}
      {selectedTest && (
        <>
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="animate-spin text-gray-400" size={32} />
            </div>
          ) : results.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <FileText size={48} className="mx-auto mb-3 opacity-30" />
              <p>No submissions found for this test</p>
            </div>
          ) : (
            <>
              {/* Test summary */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { icon: Users, label: 'Submissions', value: pagination.total, color: 'text-blue-600 bg-blue-50' },
                  { icon: Trophy, label: 'Top Score', value: results.length > 0 ? results[0].totalScore : '—', color: 'text-green-600 bg-green-50' },
                  { icon: Target, label: 'Avg Score', value: results.length > 0 ? Math.round(results.reduce((a, r) => a + r.totalScore, 0) / results.length) : '—', color: 'text-violet-600 bg-violet-50' },
                  { icon: Clock, label: 'Max Score', value: selectedTestObj?.totalQuestions || '—', color: 'text-amber-600 bg-amber-50' },
                ].map(({ icon: Icon, label, value, color }) => (
                  <div key={label} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
                      <Icon size={20} />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">{label}</p>
                      <p className="text-lg font-bold text-gray-900">{value}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Student results */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th className="text-left px-4 py-3 text-gray-500 font-semibold text-xs">#</th>
                      <th className="text-left px-4 py-3 text-gray-500 font-semibold text-xs">Rank</th>
                      <th className="text-left px-4 py-3 text-gray-500 font-semibold text-xs">Student</th>
                      <th className="text-left px-4 py-3 text-gray-500 font-semibold text-xs">Score</th>
                      <th className="text-left px-4 py-3 text-gray-500 font-semibold text-xs">Accuracy</th>
                      <th className="text-left px-4 py-3 text-gray-500 font-semibold text-xs">Time</th>
                      <th className="text-right px-4 py-3 text-gray-500 font-semibold text-xs">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((r, i) => (
                      <React.Fragment key={r._id}>
                        <tr
                          className={`border-b border-gray-50 cursor-pointer transition-colors ${
                            expandedStudent === r.student?._id ? 'bg-blue-50' : 'hover:bg-gray-50'
                          }`}
                          onClick={() => fetchStudentQuestions(r.student?._id)}
                        >
                          <td className="px-4 py-3 text-gray-400">{(page - 1) * 50 + i + 1}</td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold bg-gray-100 text-gray-700">
                              {r.rank || '—'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              {r.student?.photo ? (
                                <img src={r.student.photo} alt="" className="w-7 h-7 rounded-full object-cover" />
                              ) : (
                                <div className="w-7 h-7 bg-gray-100 rounded-full flex items-center justify-center text-xs font-semibold text-gray-600">
                                  {r.student?.name?.charAt(0)?.toUpperCase()}
                                </div>
                              )}
                              <div>
                                <p className="font-medium text-gray-800 text-xs">{r.student?.name || 'Unknown'}</p>
                                <p className="text-[10px] font-mono text-gray-400">{r.student?.hallTicketNumber || '—'}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="font-bold text-sm">{r.totalScore}<span className="text-gray-400 font-normal text-xs">/{r.maxScore}</span></span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                              r.accuracy >= 70 ? 'bg-green-100 text-green-700' :
                              r.accuracy >= 40 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-600'
                            }`}>{r.accuracy}%</span>
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-500">
                            {Math.floor(r.timeTaken / 60)}m {r.timeTaken % 60}s
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={e => { e.stopPropagation(); downloadCSV(r.student?._id, r.student?.hallTicketNumber); }}
                              className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-black transition-colors px-2 py-1 rounded-lg hover:bg-gray-100"
                            >
                              <Download size={13} /> CSV
                            </button>
                          </td>
                        </tr>

                        {/* Expanded response sheet */}
                        {expandedStudent === r.student?._id && (
                          <tr>
                            <td colSpan={7} className="p-0 bg-gray-50">
                              {questionsLoading ? (
                                <div className="flex items-center justify-center py-12">
                                  <Loader2 className="animate-spin text-gray-400" size={24} />
                                </div>
                              ) : expandedQuestions ? (
                                <div className="p-4 space-y-4">
                                  {/* Quick stats */}
                                  <div className="flex gap-4 flex-wrap text-xs">
                                    <span className="flex items-center gap-1"><CheckCircle size={13} className="text-green-500" /> Correct: <b>{expandedQuestions.result.correctAnswers}</b></span>
                                    <span className="flex items-center gap-1"><XCircle size={13} className="text-red-500" /> Wrong: <b>{expandedQuestions.result.incorrectAnswers}</b></span>
                                    <span className="flex items-center gap-1"><Minus size={13} className="text-gray-400" /> Skipped: <b>{expandedQuestions.result.unattempted}</b></span>
                                  </div>

                                  {/* Questions table */}
                                  <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
                                    <table className="w-full text-xs">
                                      <thead className="bg-gray-50 border-b">
                                        <tr>
                                          <th className="text-left px-3 py-2 text-gray-500 font-semibold">Q.No</th>
                                          <th className="text-left px-3 py-2 text-gray-500 font-semibold">Subject</th>
                                          <th className="text-left px-3 py-2 text-gray-500 font-semibold">Your Answer</th>
                                          <th className="text-left px-3 py-2 text-gray-500 font-semibold">Correct</th>
                                          <th className="text-left px-3 py-2 text-gray-500 font-semibold">Status</th>
                                          <th className="text-center px-3 py-2 text-gray-500 font-semibold">Marks</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {expandedQuestions.questions.map((q, qi) => (
                                          <tr key={qi} className={`border-b border-gray-50 ${qi % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                                            <td className="px-3 py-2 font-medium">{q.questionNumber}</td>
                                            <td className="px-3 py-2">
                                              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${SUBJECT_COLORS[q.subject] || 'bg-gray-100'}`}>
                                                {q.subject}
                                              </span>
                                            </td>
                                            <td className="px-3 py-2 font-mono">{q.selectedAnswer || '—'}</td>
                                            <td className="px-3 py-2 font-mono">{q.correctAnswer}</td>
                                            <td className="px-3 py-2">
                                              {q.selectedAnswer
                                                ? (q.isCorrect
                                                    ? <span className="text-green-600 font-medium">Correct</span>
                                                    : <span className="text-red-600 font-medium">Wrong</span>)
                                                : <span className="text-gray-400">Skipped</span>
                                              }
                                            </td>
                                            <td className="px-3 py-2 text-center font-bold">
                                              <span className={q.marks > 0 ? 'text-green-600' : (q.marks < 0 ? 'text-red-600' : 'text-gray-400')}>
                                                {q.marks > 0 ? `+${q.marks}` : q.marks}
                                              </span>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              ) : null}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>

                {/* Pagination */}
                {pagination.pages > 1 && (
                  <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
                    <p className="text-sm text-gray-500">
                      Showing {(page - 1) * 50 + 1}–{Math.min(page * 50, pagination.total)} of {pagination.total}
                    </p>
                    <div className="flex gap-2">
                      <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                        className="btn-secondary py-1.5 px-3 disabled:opacity-40 text-xs">Prev</button>
                      <button disabled={page >= pagination.pages} onClick={() => setPage(p => p + 1)}
                        className="btn-secondary py-1.5 px-3 disabled:opacity-40 text-xs">Next</button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
